import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Clock, BookOpen, Brain, BarChart, Zap } from 'lucide-react';

const curriculumModules = [
  {
    id: 1,
    title: "Introduction to Machine Learning",
    description: "Understand the fundamentals, types of ML, and real-world applications",
    duration: "2 hours",
    lessons: 8,
    topics: ["What is ML?", "Types of Learning", "Applications", "Python Setup"],
    icon: BookOpen,
    difficulty: "Beginner",
    completed: true
  },
  {
    id: 2,
    title: "Data Preprocessing & Analysis",
    description: "Learn to clean, transform, and analyze data for ML models",
    duration: "3 hours",
    lessons: 12,
    topics: ["Data Cleaning", "Feature Engineering", "EDA", "Data Visualization"],
    icon: BarChart,
    difficulty: "Beginner",
    completed: true
  },
  {
    id: 3,
    title: "Supervised Learning - Regression",
    description: "Master linear and polynomial regression techniques",
    duration: "4 hours",
    lessons: 15,
    topics: ["Linear Regression", "Polynomial Regression", "Regularization", "Model Evaluation"],
    icon: Brain,
    difficulty: "Intermediate",
    completed: false
  },
  {
    id: 4,
    title: "Supervised Learning - Classification",
    description: "Explore classification algorithms and their applications",
    duration: "5 hours",
    lessons: 18,
    topics: ["Logistic Regression", "Decision Trees", "SVM", "Random Forest"],
    icon: Zap,
    difficulty: "Intermediate",
    completed: false
  },
  {
    id: 5,
    title: "Unsupervised Learning",
    description: "Discover clustering and dimensionality reduction techniques",
    duration: "4 hours",
    lessons: 14,
    topics: ["K-Means", "Hierarchical Clustering", "PCA", "t-SNE"],
    icon: Brain,
    difficulty: "Intermediate",
    completed: false
  },
  {
    id: 6,
    title: "Neural Networks & Deep Learning",
    description: "Introduction to neural networks and deep learning concepts",
    duration: "6 hours",
    lessons: 20,
    topics: ["Perceptron", "Backpropagation", "CNN", "RNN"],
    icon: Zap,
    difficulty: "Advanced",
    completed: false
  },
  {
    id: 7,
    title: "Model Evaluation & Deployment",
    description: "Learn to evaluate models and deploy them in production",
    duration: "3 hours",
    lessons: 10,
    topics: ["Cross-validation", "Metrics", "Hyperparameter Tuning", "Deployment"],
    icon: CheckCircle,
    difficulty: "Advanced",
    completed: false
  },
  {
    id: 8,
    title: "Capstone Project",
    description: "Apply your knowledge in a comprehensive real-world project",
    duration: "8 hours",
    lessons: 5,
    topics: ["Project Planning", "Implementation", "Testing", "Presentation"],
    icon: BookOpen,
    difficulty: "Advanced",
    completed: false
  }
];

const getDifficultyColor = (difficulty: string) => {
  switch (difficulty) {
    case 'Beginner': return 'bg-green-100 text-green-700';
    case 'Intermediate': return 'bg-yellow-100 text-yellow-700';
    case 'Advanced': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export const Curriculum = () => {
  const completedModules = curriculumModules.filter(module => module.completed).length;
  const totalModules = curriculumModules.length;
  const progressPercentage = (completedModules / totalModules) * 100;

  return (
    <section id="curriculum" className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Course <span className="text-blue-600">Curriculum</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            A comprehensive 8-module curriculum designed to take you from beginner to advanced ML practitioner
          </p>
          
          {/* Progress Overview */}
          <div className="mt-8 max-w-md mx-auto">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Course Progress</span>
              <span className="text-sm text-muted-foreground">{completedModules}/{totalModules} modules</span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {curriculumModules.map((module) => {
            const IconComponent = module.icon;
            return (
              <Card 
                key={module.id} 
                className={`relative transition-all duration-300 hover:shadow-lg ${
                  module.completed ? 'border-green-200 bg-green-50/30' : 'hover:border-blue-200'
                }`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        module.completed ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        <IconComponent className={`h-5 w-5 ${
                          module.completed ? 'text-green-600' : 'text-blue-600'
                        }`} />
                      </div>
                      <div>
                        <CardTitle className="text-lg leading-tight">{module.title}</CardTitle>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant="outline" className={getDifficultyColor(module.difficulty)}>
                            {module.difficulty}
                          </Badge>
                          {module.completed && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">{module.description}</p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{module.duration}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <BookOpen className="h-4 w-4 text-muted-foreground" />
                        <span>{module.lessons} lessons</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Key Topics:</p>
                    <div className="flex flex-wrap gap-1">
                      {module.topics.map((topic, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-4">Ready to Start Your ML Journey?</h3>
            <p className="text-muted-foreground mb-6">
              Join thousands of students who have successfully completed this course
            </p>
            <div className="flex justify-center space-x-8 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">40+</div>
                <div className="text-sm text-muted-foreground">Hours of Content</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">100+</div>
                <div className="text-sm text-muted-foreground">Practical Exercises</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">5</div>
                <div className="text-sm text-muted-foreground">Real Projects</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};